import AutoBind from 'auto-bind'
import EventEmitter from 'events'
import Prefix from 'prefix'
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
gsap.registerPlugin(ScrollToPlugin);

import Text from '../animations/Text'
import Paragraph from '../animations/Paragraph'

import OverflowScrollLoop from '../components/OverflowScrollLoop'
import ScrollButtons from '../components/ScrollButtons'
import ReturnToTopButton from '../components/ReturnToTopButton'
import HeaderColour from '../components/HeaderColour'

import Detection from '../classes/Detection'

import { mapEach } from '../utils/dom'
import { setElHeightVariables } from '../utils/setElHeightVariables'

import each from 'lodash/each'

export default class extends EventEmitter {
  constructor ({ classes, element, elements, isScrollable = true }) {
    super()

    // AutoBind(this)

    this.classes = {
      ...classes
    }

    this.selectors = {
      element,
      elements: {
        animationsTexts: '[data-animation="text"]',
        animationsParagraphs: '[data-animation="paragraph"]',

        ...elements
      }
    }

    this.isScrollable = isScrollable

    this.transformPrefix = Prefix('transform')

  }

  create () {
    this.animations = []

    this.element = document.querySelector(this.selectors.element)
    this.elements = {}

    each(this.selectors.elements, (selector, key) => {
      if (selector instanceof window.HTMLElement || selector instanceof window.NodeList) {
        this.elements[key] = selector
      } else if (Array.isArray(selector)) {
        this.elements[key] = selector
      } else {
        this.elements[key] = this.element.querySelectorAll(selector)

        if (this.elements[key].length === 0) {
          this.elements[key] = null
        } else if (this.elements[key].length === 1) {
          this.elements[key] = this.element.querySelector(selector)
        }
      }
    })

    if (this.isScrollable) {
      this.scroll = {
        ease: 0.07,
        position: 0,
        current: 0,
        target: 0,
        limit: 0
      }
    }


    this.createAnimations()
    setElHeightVariables()

    this.overflowScrollLoop = new OverflowScrollLoop()
    this.scrollButtons = new ScrollButtons()
    this.returnToTopButton = new ReturnToTopButton()
    this.headerColour = new HeaderColour()

  }

  setScrollPos(scrollTo, scrollHistory, timing = 0, animation = new gsap.timeline({ defaults: { ease: "circ.easeIn" } })) {
    let scrollHistoryItem = scrollHistory.positions.find(x => x.id == window.location.href)
    let scrollHistoryPosition = scrollHistoryItem ? scrollHistoryItem.position : 0

    let scrollToPos = scrollHistory.scrollToSavedPosition ? scrollHistoryPosition : scrollTo.y
    // Only apply the hash-derived offset when we're actually scrolling to a
    // hash target — when restoring a saved position it shouldn't leak in
    // from a previous, unrelated hash calculation.
    let offsetY = scrollHistory.scrollToSavedPosition ? 0 : scrollTo.offsetY

    // A page's own feed-restoration logic (e.g. ArticleFeed/ProjectFeed's
    // scrollToAnchor(), run from Journal/Projects onPopState) can already
    // have a window-scroll tween in flight from before this navigation's
    // content swap. Without this, that leftover tween keeps animating
    // `window` scroll after the new page is in place, fighting this one.
    gsap.killTweensOf(window)

    if (timing <= 0) {
      // Page-transition positioning (restoring a saved position, jumping
      // straight to a hash target on the incoming page, or resetting to
      // top on a fresh nav) must be instant and invisible, tied directly
      // to the content swap. Routing this through GSAP's tween pipeline —
      // even at duration 0 — means it applies on GSAP's next tick rather
      // than synchronously, so the browser can paint one frame of the
      // incoming page at the wrong scroll position first, which is the
      // visible "jump" this replaces. A same-page hash click
      // (calcScrollToPos(1)) still wants a real smooth-scroll, so that
      // path (timing > 0) keeps using the GSAP tween below.
      let y = scrollToPos
      if (typeof y === 'string') {
        // scrollToPos is a hash selector string here, not a pixel value —
        // resolve it the same way ScrollToPlugin would have.
        const target = scrollTo.target || document.querySelector(y)
        y = target ? target.getBoundingClientRect().top + window.scrollY : 0
      }
      window.scrollTo(0, y + offsetY)
      return
    }

    animation
    .to(window, timing, {
      scrollTo: {
        y: scrollToPos,
        offsetY: offsetY
      },
    })
  }

  /**
   * Animations.
   */
  createAnimations () {
    // Text/Paragraph mutate the DOM on construction — splitting an
    // element's plain text into per-word <span> wrappers and driving
    // their reveal off a scroll-triggered IntersectionObserver. Statamic
    // Live Preview re-renders #main's content in place against freshly-
    // fetched (plain, unsplit) server HTML on every edit (see
    // initStatamicLivePreview() in index.js), regardless of which part
    // of the page actually changed. Reconciling split-span state against
    // that on every keystroke means either the split gets clobbered
    // (content reverts to plain text) or every reveal instance has to be
    // torn down and rebuilt wholesale (every intro replays, even for
    // untouched elements) — neither of which is what a content editor
    // wants. Skip the whole reveal system here instead: content renders
    // in its final, static state, so an edit is just a plain-text diff
    // morphdom already handles correctly on its own, and nothing ever
    // has cause to replay.
    const isLivePreview = document.documentElement.classList.contains('is-live-preview')

    if (!isLivePreview) {
      this.animationsTexts = mapEach(this.elements.animationsTexts, element => {
        return new Text({ element })
      })

      this.animations.push(...this.animationsTexts)

      console.log('this.animationsTexts', this.animationsTexts)

      this.animationsParagraphs = mapEach(this.elements.animationsParagraphs, element => {
        return new Paragraph({ element })
      })

      this.animations.push(...this.animationsParagraphs)
      console.log('this.animationsParagraphs', this.animationsParagraphs)
    }

    // marqueeButtons
    if (document.querySelector('[data-marquee-button]')) {
      import(
        '../elements/ButtonMarquee.js' /* webpackChunkName: "elements/buttonMarquee'" */
      ).then((MarqueeButton) => {
        this.marqueeButtons = new MarqueeButton.default();
      })
    }

  }

  async show (animation = new gsap.timeline()) {
    console.log('Show BASE function', this.element)
    return new Promise(resolve => {
      animation.call(() => {
        resolve()
      })

      this.addEventListeners()
    })

  }

  async hide (animation = new gsap.timeline({})) {
    console.log('Hide BASE function')
    return new Promise(resolve => {
      animation.call(() => {
        resolve()
      })
    })
  }

  animateOut () {
    this.animations.forEach(animation => {
      animation.animateOut()
    })
  }

  /**
   * Events.
   */
  onResize () {
    if (!this.isScrollable || !this.elements.wrapper) return

    this.scroll.limit = this.elements.wrapper.clientHeight - window.innerHeight

    this.update()

    this.animations.forEach(animation => {
      animation.onResize && animation.onResize()
    })
  }

  onTouchDown (event) {
    if (!Detection.isPhone()) return

    this.isDown = true

    this.scroll.position = this.scroll.current
    this.start = event.touches ? event.touches[0].clientY : event.clientY
  }

  onTouchMove (event) {
    if (!Detection.isPhone() || !this.isDown) return

    const y = event.touches ? event.touches[0].clientY : event.clientY
    const distance = (this.start - y) * 3

    this.scroll.target = this.scroll.position + distance
  }

  onTouchUp (event) {
    if (!Detection.isPhone()) return

    this.isDown = false
  }

  onWheel (normalized) {
    const speed = normalized.pixelY

    this.scroll.target += speed

    return speed
  }

  reset () {

  }

  /**
   * Listeners.
   */
  addEventListeners () {

  }

  removeEventListeners () {
    
  }

  /**
   * Destroy.
   */
  destroy () {
    if (document.querySelector('[data-marquee-button]') && this.marqueeButtons)  {
      this.marqueeButtons.destroy()
    }

    if (document.querySelector('[data-overflow-scroll]')) {
      this.overflowScrollLoop.destroy()
    }

    if (document.querySelector('[data-scroll-button]')) {
      this.scrollButtons.destroy()
    }

    if (document.querySelector('[data-top-button]')) {
      this.returnToTopButton.destroy()
    }

    if (document.querySelector('[data-header-invert]')) {
      this.headerColour.destroy()
    }

  }

}
